// src/pages/Signup.js
import { logger } from "../utils/logger";
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  createUserWithEmailAndPassword,
  updateProfile,
  sendEmailVerification,
} from 'firebase/auth';
import { auth, db } from '../firebase';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import { CONTINENTS, COUNTRIES_BY_CONTINENT, mapContinentValueToName } from '../utils/continentsData';
import { TIMEZONES, getDefaultTimezone, findTimezone } from '../utils/timezones';
import { ROLE_VALUE } from "../constants/roleLabels";
import { BRAND } from "../config/brand";
import BrandMark from "../components/BrandMark";
import {
  User,
  Mail,
  Lock,
  Eye,
  EyeOff,
  MapPin,
  Building,
  FileText,
  Brain,
  Shield,
  Zap,
  Check,
  ArrowRight,
  Users,
  ChevronDown,
} from 'lucide-react';

function Signup() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'requester',
    bio: '',
    continent: '',
    country: '',
    city: '',
    company: '',
    timezone: getDefaultTimezone(),
    isAdult: false,
  });

  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [ageError, setAgeError] = useState('');

  const navigate = useNavigate();
  const { setCurrentUserRole } = useAuth();

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => {
      // Reset country and city when continent changes
      if (name === 'continent') {
        return { ...prev, continent: value, country: '', city: '' };
      }
      // Reset city when country changes
      if (name === 'country') {
        return { ...prev, country: value, city: '' };
      }
      if (type === 'checkbox') {
        // Clear age error when checkbox toggles
        if (name === 'isAdult') {
          setAgeError('');
        }
        return { ...prev, [name]: checked };
      }
      return { ...prev, [name]: value };
    });
  };

  const handleRoleSelect = (role) => {
    setFormData((prev) => ({ ...prev, role }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (loading) return;

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters.');
      return;
    }

    if (!formData.isAdult) {
      toast.error('You must confirm that you are at least 18 years old to register.');
      setAgeError('You must confirm that you are at least 18 years old to register.');
      return;
    }

    setAgeError('');
    setLoading(true);

    if (!['requester', 'specialist'].includes(formData.role)) {
      toast.error('Invalid role selected.');
      setLoading(false);
      return;
    }

    // Validate location fields for clients only
    if (
      formData.role === 'requester' &&
      (!formData.continent || !formData.country || !formData.city?.trim() || !formData.timezone)
    ) {
      toast.error('Please provide your complete location (Continent, Country, City, and Timezone).');
      setLoading(false);
      return;
    }

    try {
      // 1️⃣ Create user in Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );
      const user = userCredential.user;
      logger.log('✅ User created in Auth:', user.uid);

      // 2️⃣ Update display name in Firebase Auth
      await updateProfile(user, { displayName: formData.name });
      logger.log('✅ User displayName updated:', user.displayName);

      // 3️⃣ Build structured location from form data
    const selectedTimezone = formData.role === 'requester' 
        ? findTimezone(formData.timezone) 
        : getDefaultTimezone();
      const locationData =
        formData.role === 'requester'
          ? {
              city: formData.city || '',
              country: formData.country || '',
              continent: mapContinentValueToName(formData.continent) || '',
              timezone: selectedTimezone,
            }
          : {
              city: '',
              country: '',
              continent: '',
              timezone: selectedTimezone,
            };
      
      // 4️⃣ Get tags from FreeDiagnosis if available
      const diagnosisCategory = localStorage.getItem('pendingDiagnosisCategory');
      const userTags = diagnosisCategory ? [diagnosisCategory] : [];
      
      // 5️⃣ Persist user profile immediately with verification flag
      const userDocRef = doc(db, 'users', user.uid);
      await setDoc(
        userDocRef,
        {
          uid: user.uid,
          name: formData.name || user.displayName || 'Unnamed',
          email: formData.email,
          role: formData.role,
          bio: formData.bio || '',
          location: locationData,
          tags: userTags,
          company: formData.role === 'requester' ? (formData.company || '') : '',
          emailVerified: user.emailVerified || false,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      
      if (formData.role === 'specialist') {
        const helperDraftRef = doc(db, 'helpersDraft', user.uid);
        const helperProfileRef = doc(db, 'helpers', user.uid);
        await setDoc(
          helperDraftRef,
          {
            step: 1,
            completedInitialSetup: false,
            basicInfo: {
              name: formData.name || user.displayName || '',
              primarySkills: [],
            },
            experience: {
              years: '',
              portfolioLinks: [],
            },
            availability: {
              timezone: selectedTimezone,
              availabilityStatus: 'available',
            },
            advancedDetails: {
              hourlyRate: '',
              extendedBio: '',
              certifications: [],
            },
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );

        await setDoc(
          helperProfileRef,
          {
            uid: user.uid,
            step: 1,
            completedInitialSetup: false,
            basicInfo: {
              name: formData.name || user.displayName || '',
              primarySkills: [],
            },
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
      }
      
      // Clear diagnosis category after use
      if (diagnosisCategory) {
        localStorage.removeItem('pendingDiagnosisCategory');
      }

      // 6️⃣ Send Firebase email verification
      try {
        await sendEmailVerification(user);
        // Store timestamp to prevent duplicate sends in VerifyEmail page
        localStorage.setItem('verificationEmailSent', Date.now().toString());
        toast.info('Verification email sent. Please check your inbox.');
      } catch (emailError) {
        console.error('Email verification error:', emailError);
        // Don't fail the signup if email sending fails - user can resend later
        if (emailError.code === 'auth/too-many-requests') {
          toast.warning('Verification email rate limited. Please use the resend button on the next page.');
        } else {
          toast.warning('Account created, but verification email could not be sent. Please use resend on the next page.');
        }
      }

      // 7️⃣ Save role locally & in context
      const canonicalRole =
        formData.role === ROLE_VALUE.specialist
          ? ROLE_VALUE.specialist
          : ROLE_VALUE.requester;
      localStorage.setItem('userRole', canonicalRole);
      setCurrentUserRole(canonicalRole);

      // 7.1️⃣ Cache current user for smoother reloads
      localStorage.setItem(
        'currentUser',
        JSON.stringify({
          uid: user.uid,
          email: formData.email,
          name: formData.name || user.displayName || 'User',
          role: canonicalRole,
        })
      );

      // 8️⃣ Redirect user to verify page
      navigate('/verify-email');
    } catch (error) {
      console.error('Signup error:', error);

      let errorMessage = 'Signup failed. Please try again.';
      if (error.code === 'auth/email-already-in-use') {
        errorMessage = 'This email is already registered.';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email format.';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'Password is too weak.';
      } else if (error.code === 'auth/network-request-failed') {
        errorMessage = 'Network error. Please check your connection.';
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = 'Too many requests. Please wait a moment and try again.';
      } else if (error.code === 'permission-denied') {
        errorMessage = 'Your account was created, but profile setup is temporarily blocked by permissions. Please try again in a few seconds.';
      } else {
        // Log unexpected errors for debugging
        console.error('Unexpected signup error:', error.code, error.message);
        errorMessage = error.message || 'Signup failed. Please try again.';
      }

      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center py-8 sm:py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl w-full">
        {/* Mobile Branding Header */}
        <div className="lg:hidden text-center mb-6">
          <div className="inline-flex items-center justify-center mb-4">
            <BrandMark size={56} rounded="rounded-2xl" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Join {BRAND.name}</h1>
          <p className="text-sm text-slate-500 px-4">
            Join the intelligence-powered network where specialists and requesters collaborate to solve real problems fast.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Left Side - Branding (Desktop Only) */}
          <div className="hidden lg:block">
            <div className="relative bg-slate-900 rounded-2xl p-10 text-white">
              <div className="mb-8">
                <div className="inline-flex items-center justify-center mb-6">
                  <BrandMark size={64} boxed rounded="rounded-2xl" />
                </div>
                <h1 className="text-3xl font-bold text-white mb-3">
                  Join {BRAND.name}
                </h1>
                <p className="text-lg text-slate-300 leading-relaxed">
                  Join the intelligence-powered network where specialists and requesters collaborate to solve real problems fast.
                </p>
              </div>

              {/* Value Props */}
              <div className="space-y-4 mb-2">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-slate-800 border border-slate-700 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Brain className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white mb-1">Intelligence-powered matching</h3>
                    <p className="text-sm text-slate-400">
                      Get instantly connected with the right specialist for your problem.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-slate-800 border border-slate-700 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Zap className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white mb-1">Instant collaboration</h3>
                    <p className="text-sm text-slate-400">
                      Real-time workspace with chat, files, and progress tracking.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-slate-800 border border-slate-700 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Shield className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white mb-1">Secure &amp; trusted</h3>
                    <p className="text-sm text-slate-400">
                      Encrypted data and secure authentication.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Signup Form */}
          <div className="w-full">
            <div className="bg-white rounded-2xl shadow-sm p-6 sm:p-8 md:p-10 max-w-md mx-auto border border-slate-200">
              {/* Header (Hidden on mobile since we have top branding) */}
              <div className="text-center mb-6 sm:mb-8">
                <div className="hidden sm:inline-flex items-center justify-center mb-4">
                  <BrandMark size={56} rounded="rounded-2xl" />
                </div>
                <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">Sign up</h2>
                <p className="text-slate-500 text-xs sm:text-sm leading-relaxed">
                  Create your {BRAND.name} account and start solving today.
                </p>
              </div>

              {/* Join Message */}
              <div className="mb-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
                <p className="text-sm text-slate-600 text-center">
                  Join specialists and requesters solving real problems with {BRAND.name}.
                </p>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Full Name */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Full Name
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type="text"
                      name="name"
                      placeholder="Enter your full name"
                      className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition"
                      value={formData.name}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Email
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type="email"
                      name="email"
                      placeholder="you@example.com"
                      className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition"
                      value={formData.email}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                {/* Password */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      placeholder="Minimum 6 characters"
                      className="w-full pl-10 pr-12 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition"
                      value={formData.password}
                      onChange={handleChange}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600 transition"
                    >
                      {showPassword ? (
                        <EyeOff className="w-5 h-5" />
                      ) : (
                        <Eye className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Role Selection - Cards */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-3">
                    I am...
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => handleRoleSelect('requester')}
                      className={`p-4 rounded-xl border-2 transition ${
                        formData.role === 'requester'
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <div className="flex flex-col items-center gap-2">
                        <div
                          className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            formData.role === 'requester' ? 'bg-blue-600' : 'bg-slate-100'
                          }`}
                        >
                          <Users className={`w-5 h-5 ${formData.role === 'requester' ? 'text-white' : 'text-slate-400'}`} />
                        </div>
                        <span className={`text-sm font-semibold ${formData.role === 'requester' ? 'text-blue-700' : 'text-slate-700'}`}>
                          I need help
                        </span>
                        <span className="text-xs text-slate-500">(Requester)</span>
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleRoleSelect('specialist')}
                      className={`p-4 rounded-xl border-2 transition ${
                        formData.role === 'specialist'
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <div className="flex flex-col items-center gap-2">
                        <div
                          className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            formData.role === 'specialist' ? 'bg-blue-600' : 'bg-slate-100'
                          }`}
                        >
                          <Zap className={`w-5 h-5 ${formData.role === 'specialist' ? 'text-white' : 'text-slate-400'}`} />
                        </div>
                        <span className={`text-sm font-semibold ${formData.role === 'specialist' ? 'text-blue-700' : 'text-slate-700'}`}>
                          I can help
                        </span>
                        <span className="text-xs text-slate-500">(Specialist)</span>
                      </div>
                    </button>
                  </div>
                  {formData.role === 'specialist' && (
                    <p className="mt-2 text-xs text-blue-600 text-center">
                    You'll finish onboarding right after signing up
                    </p>
                  )}
                </div>

                {/* Short Bio */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Short Bio <span className="text-slate-400 font-normal">(optional)</span>
                  </label>
                  <div className="relative">
                    <FileText className="absolute left-3 top-3 w-5 h-5 text-slate-400" />
                    <textarea
                      name="bio"
                      placeholder="Tell us about yourself..."
                      className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition h-20 resize-none"
                      value={formData.bio}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                {/* Location fields (clients only) */}
                {formData.role === 'requester' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Continent <span className="text-rose-500">*</span>
                      </label>
                      <div className="relative">
                        <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400 z-10" />
                        <select
                          name="continent"
                          className="w-full pl-10 pr-10 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition appearance-none bg-white cursor-pointer"
                          value={formData.continent}
                          onChange={handleChange}
                          required={formData.role === 'requester'}
                        >
                          <option value="">Select Continent</option>
                          {CONTINENTS.map((continent) => (
                            <option key={continent.value} value={continent.value}>
                              {continent.label}
                            </option>
                          ))}
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
                      </div>
                    </div>

                    {formData.continent && (
                      <div className="relative">
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Country <span className="text-rose-500">*</span>
                        </label>
                        <select
                          name="country"
                          className="w-full px-4 pr-10 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition appearance-none bg-white cursor-pointer"
                          value={formData.country}
                          onChange={handleChange}
                          required={formData.role === 'requester'}
                        >
                          <option value="">Select Country</option>
                          {COUNTRIES_BY_CONTINENT[formData.continent]?.map((country) => (
                            <option key={country.value} value={country.value}>
                              {country.label}
                            </option>
                          ))}
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
                      </div>
                    )}

                    {formData.country && (
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          City <span className="text-rose-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="city"
                          placeholder="e.g., Berlin"
                          className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition"
                          value={formData.city}
                          onChange={handleChange}
                          required={formData.role === 'requester'}
                        />
                      </div>
                    )}

                    {/* Timezone - always visible for clients after continent is selected */}
                    {formData.continent && (
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Timezone <span className="text-rose-500">*</span>
                        </label>
                        <div className="relative">
                          <select
                            name="timezone"
                            className="w-full px-4 pr-10 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition appearance-none bg-white cursor-pointer"
                            value={formData.timezone}
                            onChange={handleChange}
                            required={formData.role === 'requester'}
                          >
                            {TIMEZONES.map((tz) => (
                              <option key={tz.value} value={tz.value}>
                                {tz.label}
                              </option>
                            ))}
                          </select>
                          <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
                        </div>
                        <p className="text-xs text-slate-500 mt-1">
                          Select your timezone for better matching with experts.
                        </p>
                      </div>
                    )}
                  </>
                )}

                {/* Company (only for clients) */}
                {formData.role === 'requester' && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Company <span className="text-slate-400 font-normal">(optional)</span>
                    </label>
                    <div className="relative">
                      <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <input
                        type="text"
                        name="company"
                        placeholder="Your company name"
                        className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition"
                        value={formData.company}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                )}

                {/* Role-based Conversion Message */}
                {formData.role === 'requester' && (
                  <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <p className="text-xs text-slate-600 text-center">
                      Get priority access to experts after signup
                    </p>
                  </div>
                )}
                {formData.role === 'specialist' && (
                  <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <p className="text-xs text-slate-600 text-center">
                      Get started after verification
                    </p>
                  </div>
                )}

                {/* Age Confirmation */}
                <div className="space-y-2">
                  <label className="flex items-start gap-3 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      name="isAdult"
                      checked={formData.isAdult}
                      onChange={handleChange}
                      className="mt-1 h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                      required
                    />
                    <span className="leading-6">
                      I confirm I am at least 18 years old
                      <span className="block text-xs text-slate-500">
                        You confirm that you are 18 years old or older.
                      </span>
                    </span>
                  </label>
                  {ageError && <p className="text-sm text-rose-600">{ageError}</p>}
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-all shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Creating account...
                    </>
                  ) : (
                    <>
                      Sign Up
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </form>

              {/* Trust Indicators */}
              <div className="mt-6 pt-6 border-t border-slate-200">
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-xs text-slate-600">
                    <Check className="w-3 h-3 text-emerald-500" />
                    <span>Secure authentication (Firebase)</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-slate-600">
                    <Shield className="w-3 h-3 text-blue-500" />
                    <span>Data encrypted and private</span>
                  </div>
                </div>
              </div>

              {/* Login Link */}
              <div className="mt-6 text-center">
                <p className="text-sm text-slate-600">
                  Already have an account?{' '}
                  <Link
                    to="/login"
                    className="text-blue-600 hover:text-blue-700 font-semibold transition"
                  >
                    Sign in
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Signup;
